/**
 * Integration Provider Catalog — system-defined, not user-extensible.
 *
 * Analogous to ProcessNodes (ADR-015): the set of available providers is
 * fixed in code. Users can connect/disconnect but cannot invent new providers.
 * "Request integration" is the escape hatch.
 *
 * 2026-05-25 cleanup (草台 bug sweep, Bug 12):
 *   - Dropped China-only providers (HR and messaging suites outside the
 *     target markets). Owner directive: not pursuing the China market.
 *   - Marked everything backend doesn't actually wire as ``comingSoon``.
 *     Pre-fix the catalog showed 19 connect-able providers but only
 *     Slack + Teams had backend implementations; clicking the others
 *     created an inert connection row that did nothing — read as 草台.
 *   - Workday + BambooHR called out as priority Coming Soon items
 *     (owner: Workday is 重中之重 / highest priority).
 *
 * Each provider defines:
 *   - key: unique slug matching backend ``IntegrationConnection.provider``
 *   - name: display name
 *   - category: for tab filtering in the Connectors marketplace
 *   - description: one-liner
 *   - dataFlow: 'one_way_push' | 'one_way_pull' | 'bidirectional'
 *   - configFields: what the Connect drawer asks the user to fill in
 *   - comingSoon: true → render disabled with the Coming Soon ribbon,
 *     no Connect button. Frontend-only flag — backend is unaware.
 */

export type ProviderCategory = 'messaging' | 'hr_payroll' | 'finance_erp' | 'storage';
export type DataFlow = 'one_way_push' | 'one_way_pull' | 'bidirectional';

export interface ConfigField {
  key: string;
  i18nKey: string;
  type: 'text' | 'url' | 'password' | 'select';
  placeholder?: string;
  required?: boolean;
  options?: { value: string; i18nKey: string }[];
}

export interface ProviderDefinition {
  key: string;
  name: string;
  nameKey: string;
  icon: string;
  category: ProviderCategory;
  description: string;
  descriptionKey: string;
  dataFlow: DataFlow;
  popular?: boolean;
  comingSoon?: boolean;
  configFields: ConfigField[];
  /**
   * P0.1 Path A3: install via OAuth2 (Slack, Teams).
   * When true, the dialog skips the configFields form and the
   * "Connect" button hands off to
   * `GET /v1/integrations/{key}/oauth/start`. Provider-side install
   * UI handles channel pick + auth.
   */
  oauthFlow?: boolean;
  /**
   * Minimum plan tier required (matches backend `IMProvider.min_tier`).
   * The dialog reads this for plan-gate UI hints; the real gate is
   * server-side (402 license_required).
   */
  minTier?: 'free' | 'starter' | 'professional' | 'enterprise';
  /**
   * 2026-05-25 Bug 23b — link to the public-facing setup guide on
   * svdy.com. Surfaced as a 'View setup guide' link in the Connectors
   * dialog so SMB-novice admins get walk-through context before
   * clicking Connect.
   */
  guideUrl?: string;
}

export const CATEGORIES: { key: ProviderCategory; i18nKey: string }[] = [
  { key: 'messaging', i18nKey: 'integrations:catalog.categories.messaging' },
  { key: 'hr_payroll', i18nKey: 'integrations:catalog.categories.hr_payroll' },
  { key: 'finance_erp', i18nKey: 'integrations:catalog.categories.finance_erp' },
  { key: 'storage', i18nKey: 'integrations:catalog.categories.storage' },
];

export const integrationCatalog: ProviderDefinition[] = [
  // ─── Messaging ───────────────────────────────────────────────────────
  {
    key: 'slack',
    name: 'Slack',
    nameKey: 'integrations:catalog.providers.slack.name',
    icon: 'Tag',
    category: 'messaging',
    description: 'Post attendance alerts and notifications to Slack channels.',
    descriptionKey: 'integrations:catalog.providers.slack.description',
    dataFlow: 'one_way_push',
    popular: true,
    oauthFlow: true,
    minTier: 'starter',
    configFields: [],
    guideUrl: 'https://svdy.com/integrations/slack',
  },
  {
    key: 'teams',
    name: 'Microsoft Teams',
    nameKey: 'integrations:catalog.providers.teams.name',
    icon: 'Groups',
    category: 'messaging',
    description: 'Send attendance alerts and notifications to Teams channels (Workflows webhook).',
    descriptionKey: 'integrations:catalog.providers.teams.description',
    dataFlow: 'one_way_push',
    popular: true,
    oauthFlow: false,
    minTier: 'professional',
    configFields: [
      { key: 'webhook_url', i18nKey: 'integrations:catalog.configFields.webhookUrl', type: 'url', placeholder: 'https://<tenant>.webhook.office.com/webhookb2/...', required: true },
    ],
    guideUrl: 'https://svdy.com/integrations/teams',
  },
  {
    key: 'whatsapp',
    name: 'WhatsApp',
    nameKey: 'integrations:catalog.providers.whatsapp.name',
    icon: 'Chat',
    category: 'messaging',
    description: 'Send clock-in reminders, late alerts, and leave notifications to workers via WhatsApp.',
    descriptionKey: 'integrations:catalog.providers.whatsapp.description',
    dataFlow: 'one_way_push',
    popular: true,
    comingSoon: true,
    configFields: [],
  },

  // ─── HR & Payroll ────────────────────────────────────────────────────
  {
    key: 'workday',
    name: 'Workday',
    nameKey: 'integrations:catalog.providers.workday.name',
    icon: 'BusinessCenter',
    category: 'hr_payroll',
    description: 'Import worker data from Workday HCM into SVDY.',
    descriptionKey: 'integrations:catalog.providers.workday.description',
    /* `one_way_pull`, not `bidirectional` — corrected 2026-08-28.
       `WorkdayProvider` has no `push_worker` and inherits `supports_write = False`, so the
       outbound leg is gated off inside the sync engine. The connector is complete INBOUND
       (OAuth2 client-credentials token fetch, `test_connection`, `fetch_workers`, field
       mapping, all covered by tests) and has no write-back at all. BambooHR is the one that
       really is bidirectional: `supports_write = True` plus a `push_worker` implementation.
       `test_hris_plan_gate.py::test_workday_does_not_claim_write_back` pins the difference. */
    dataFlow: 'one_way_pull',
    popular: true,
    // Coming soon (owner, 2026-11-02): the connector is built and tested INBOUND, but it has never
    // been validated against a real Workday tenant and org/department import is not built. The
    // website lists it as planned; an admin must not find a live Connect button that the product
    // cannot stand behind. Remove this line once a pilot tenant has passed field-mapping validation.
    comingSoon: true,
    configFields: [
      { key: 'tenant_url', i18nKey: 'integrations:hris.workday.tenantUrl', type: 'text', placeholder: 'wd5-impl-services1.workday.com', required: true },
      { key: 'tenant_name', i18nKey: 'integrations:hris.workday.tenantName', type: 'text', placeholder: 'acme', required: true },
      { key: 'client_id', i18nKey: 'integrations:hris.workday.clientId', type: 'text', required: true },
      { key: 'client_secret', i18nKey: 'integrations:hris.workday.clientSecret', type: 'password', required: true },
    ],
    guideUrl: 'https://svdy.com/integrations',
  },
  {
    key: 'bamboohr',
    name: 'BambooHR',
    nameKey: 'integrations:catalog.providers.bamboohr.name',
    icon: 'Spa',
    category: 'hr_payroll',
    description: 'Sync worker profiles and time-off balances with BambooHR.',
    descriptionKey: 'integrations:catalog.providers.bamboohr.description',
    dataFlow: 'bidirectional',
    popular: true,
    configFields: [
      { key: 'subdomain', i18nKey: 'integrations:hris.bamboohr.subdomain', type: 'text', placeholder: 'acme', required: true },
      { key: 'api_key', i18nKey: 'integrations:hris.bamboohr.apiKey', type: 'password', required: true },
    ],
    guideUrl: 'https://svdy.com/integrations/bamboohr',
  },
  {
    key: 'adp',
    name: 'ADP',
    nameKey: 'integrations:catalog.providers.adp.name',
    icon: 'Payments',
    category: 'hr_payroll',
    description: 'Export attendance hours and overtime to ADP payroll.',
    descriptionKey: 'integrations:catalog.providers.adp.description',
    dataFlow: 'one_way_push',
    comingSoon: true,
    configFields: [],
  },
  {
    key: 'gusto',
    name: 'Gusto',
    nameKey: 'integrations:catalog.providers.gusto.name',
    icon: 'AccountBalance',
    category: 'hr_payroll',
    description: 'Send attendance hours directly to Gusto for payroll processing.',
    descriptionKey: 'integrations:catalog.providers.gusto.description',
    dataFlow: 'one_way_push',
    comingSoon: true,
    configFields: [],
  },

  // ─── Finance & ERP ───────────────────────────────────────────────────
  {
    key: 'quickbooks',
    name: 'QuickBooks',
    nameKey: 'integrations:catalog.providers.quickbooks.name',
    icon: 'Receipt',
    category: 'finance_erp',
    description: 'Export worker hours and overtime to QuickBooks for payroll.',
    descriptionKey: 'integrations:catalog.providers.quickbooks.description',
    dataFlow: 'one_way_push',
    popular: true,
    oauthFlow: true,
    // Coming soon (owner, 2026-11-02): QUICKBOOKS_CLIENT_ID/SECRET are empty in production, so
    // Connect returned `quickbooks_not_configured`. Remove once the Intuit app's production keys
    // are in Secrets Manager and an end-to-end export has passed.
    comingSoon: true,
    configFields: [],
  },
  {
    key: 'xero',
    name: 'Xero',
    nameKey: 'integrations:catalog.providers.xero.name',
    icon: 'PointOfSale',
    category: 'finance_erp',
    description: 'Sync attendance hours with Xero for payroll and invoicing.',
    descriptionKey: 'integrations:catalog.providers.xero.description',
    dataFlow: 'one_way_push',
    comingSoon: true,
    configFields: [],
  },
  {
    key: 'sap',
    name: 'SAP',
    nameKey: 'integrations:catalog.providers.sap.name',
    icon: 'Inventory',
    category: 'finance_erp',
    description: 'Integrate attendance data with SAP ERP for workforce management.',
    descriptionKey: 'integrations:catalog.providers.sap.description',
    dataFlow: 'bidirectional',
    comingSoon: true,
    configFields: [],
  },
  {
    key: 'netsuite',
    name: 'NetSuite',
    nameKey: 'integrations:catalog.providers.netsuite.name',
    icon: 'Cloud',
    category: 'finance_erp',
    description: 'Connect attendance and time tracking with Oracle NetSuite.',
    descriptionKey: 'integrations:catalog.providers.netsuite.description',
    dataFlow: 'bidirectional',
    comingSoon: true,
    configFields: [],
  },

  // ─── Storage ─────────────────────────────────────────────────────────
  {
    key: 'google_drive',
    name: 'Google Drive',
    nameKey: 'integrations:catalog.providers.google_drive.name',
    icon: 'CloudUpload',
    category: 'storage',
    description: 'Auto-export attendance reports and documents to Google Drive.',
    descriptionKey: 'integrations:catalog.providers.google_drive.description',
    dataFlow: 'one_way_push',
    popular: true,
    comingSoon: true,
    configFields: [],
  },
  {
    key: 'dropbox',
    name: 'Dropbox',
    nameKey: 'integrations:catalog.providers.dropbox.name',
    icon: 'Inbox',
    category: 'storage',
    description: 'Auto-export attendance reports to Dropbox Business.',
    descriptionKey: 'integrations:catalog.providers.dropbox.description',
    dataFlow: 'one_way_push',
    comingSoon: true,
    configFields: [],
  },
  {
    key: 'onedrive',
    name: 'OneDrive',
    nameKey: 'integrations:catalog.providers.onedrive.name',
    icon: 'CloudSync',
    category: 'storage',
    description: 'Auto-export attendance reports to OneDrive for Business.',
    descriptionKey: 'integrations:catalog.providers.onedrive.description',
    dataFlow: 'one_way_push',
    comingSoon: true,
    configFields: [],
  },
];

/** Lookup a provider by key. */
export function getProvider(key: string): ProviderDefinition | undefined {
  return integrationCatalog.find(p => p.key === key);
}

/** Filter catalog by category. */
export function getProvidersByCategory(category: ProviderCategory): ProviderDefinition[] {
  return integrationCatalog.filter(p => p.category === category);
}
